import { FormData } from "./formData";
import { PublisherStore } from "./store/adCategoryStore";

export interface ValidationError {
  field: string;
  message: string;
  stepIndex?: number;
  publisherIndex?: number;
  adInputIndex?: number;
  parameterKey?: string;
  customParamKey?: string;
}

export interface StepValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export class AdCategoryValidation {
  static validateStepOne(state: PublisherStore): StepValidationResult {
    const errors: ValidationError[] = [];

    if (!state.adCategory || state.adCategory.trim() === "") {
      errors.push({
        field: "adCategory",
        message: "Ad Category name is required",
      });
    }

    if (state.adCategory && state.adCategory.trim().length > 255) {
      errors.push({
        field: "adCategory",
        message: "Ad Category name must not exceed 255 characters",
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static validateStepTwo(state: PublisherStore): StepValidationResult {
    const errors: ValidationError[] = [];

    if (!state.publisherType || state.publisherType.trim() === "") {
      errors.push({
        field: "publisherType",
        message: "Publisher type is required",
      });
    }

    if (!state.publishers || state.publishers.length === 0) {
      errors.push({
        field: "publishers",
        message: "At least one publisher is required",
      });
      return { isValid: false, errors };
    }

    state.publishers.forEach((publisher, index) => {
      if (state.publisherType === "user_defined") {
        return;
      }

      if (!publisher || publisher.name.trim() === "") {
        errors.push({
          field: "publishers",
          message: `Publisher ${index + 1} name is required`,
          publisherIndex: index,
        });
      }

      if (publisher && publisher.name.trim().length > 255) {
        errors.push({
          field: "publishers",
          message: `Publisher ${index + 1} name must not exceed 255 characters`,
          publisherIndex: index,
        });
      }
    });

    if (state.publisherType === "multiple_publisher") {
      const publisherNames = state.publishers
        .map((p) => p.name.trim().toLowerCase())
        .filter((p) => p !== "");

      const duplicates = publisherNames.filter(
        (name, index) => publisherNames.indexOf(name) !== index
      );

      if (duplicates.length > 0) {
        errors.push({
          field: "publishers",
          message: "Duplicate publisher names are not allowed",
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static validateStepThree(state: PublisherStore): StepValidationResult {
    const errors: ValidationError[] = [];

    if (!state.adTypes || state.adTypes.length === 0) {
      errors.push({
        field: "adTypes",
        message: "Ad types are required",
      });
      return { isValid: false, errors };
    }

    state.adTypes.forEach((adType, adTypeIndex) => {
      // Skip publisher validation if publisherType is "user_defined"
      if (
        state.publisherType !== "user_defined" &&
        (!adType.publisher || adType.publisher.trim() === "")
      ) {
        errors.push({
          field: "adTypes",
          message: `Publisher is required for ad type ${adTypeIndex + 1}`,
          stepIndex: adTypeIndex,
        });
      }

      if (!adType.adInputs || adType.adInputs.length === 0) {
        errors.push({
          field: "adInputs",
          message: `At least one ad input is required for publisher "${adType.publisher}"`,
          stepIndex: adTypeIndex,
        });
        return;
      }

      adType.adInputs.forEach((adInput, adInputIndex) => {
        if (!adInput.name || adInput.name.trim() === "") {
          errors.push({
            field: "adInputName",
            message: `Ad type name is required for publisher "${
              adType.publisher
            }", input ${adInputIndex + 1}`,
            stepIndex: adTypeIndex,
            adInputIndex,
          });
        }

        if (!adInput.mediaType || adInput.mediaType.trim() === "") {
          errors.push({
            field: "mediaType",
            message: `Media type is required for ad type "${adInput.name}" under publisher "${adType.publisher}"`,
            stepIndex: adTypeIndex,
            adInputIndex,
          });
        }

        const allowedMediaTypes = [
          "print",
          "digital",
          "broadcast",
          "outdoor_digital",
          "social",
        ];
        if (
          adInput.mediaType &&
          !allowedMediaTypes.includes(adInput.mediaType)
        ) {
          errors.push({
            field: "mediaType",
            message: `Invalid media type "${adInput.mediaType}" for ad type "${adInput.name}"`,
            stepIndex: adTypeIndex,
            adInputIndex,
          });
        }
      });

      const adInputNames = adType.adInputs
        .map((input) => input.name?.trim().toLowerCase())
        .filter((name) => name && name !== "");

      const duplicateNames = adInputNames.filter(
        (name, index) => adInputNames.indexOf(name) !== index
      );

      if (duplicateNames.length > 0) {
        errors.push({
          field: "adInputs",
          message: `Duplicate ad type names found for publisher "${adType.publisher}"`,
          stepIndex: adTypeIndex,
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static validateStepFour(state: PublisherStore, baseKeyPrefix?: string): StepValidationResult {
    const errors: ValidationError[] = [];

    const filteredParams = baseKeyPrefix
      ? state.parametersInput.filter((p) => p.key.startsWith(baseKeyPrefix + "_"))
      : state.parametersInput;

    if (filteredParams && filteredParams.length > 0) {
      filteredParams.forEach((param) => {
        // Skip validation if parameter is disabled
        if (param.enabled === false) return;

        if (!param.settings || param.settings.trim() === "") {
          errors.push({
            field: "parameterSettings",
            message: `Setting is required for parameter "${param.key}"`,
            parameterKey: param.key,
          });
        }

        if (param.settings !== "user_defined") {
          param.inputs.forEach((input, inputIndex) => {
            Object.keys(input).forEach((fieldKey) => {
              const value = input[fieldKey];
              if (fieldKey === "settings") return;

              if (
                param.settings !== "user_defined" &&
                (!value || value.toString().trim() === "")
              ) {
                errors.push({
                  field: "parameterInput",
                  message: `${fieldKey} is required for parameter "${
                    param.key
                  }"${
                    param.settings === "multiple"
                      ? ` (entry ${inputIndex + 1})`
                      : ""
                  }`,
                  parameterKey: param.key,
                });
              }
            });
          });
        }

        if (param.settings === "multiple" && param.inputs.length === 0) {
          errors.push({
            field: "parameterInput",
            message: `At least one entry is required for parameter "${param.key}"`,
            parameterKey: param.key,
          });
        }
      });
    }

    const filteredCustomParams = baseKeyPrefix
      ? state.customParameters.filter(
          (cp) => cp.key.split("_")[0] + "_" + cp.key.split("_")[1] === baseKeyPrefix,
        )
      : state.customParameters;

    if (filteredCustomParams && filteredCustomParams.length > 0) {
      filteredCustomParams.forEach((customParam) => {
        // Skip validation if custom parameter is disabled
        if (customParam.enabled === false) return;

        if (!customParam.name || customParam.name.trim() === "") {
          errors.push({
            field: "customParameterName",
            message: "Custom parameter name is required",
            customParamKey: customParam.key,
          });
        }

        if (!customParam.type) {
          errors.push({
            field: "customParameterType",
            message: `Parameter type is required for custom parameter "${
              customParam.name || "Untitled"
            }"`,
            customParamKey: customParam.key,
          });
        }

        if (customParam.type === "dropdown") {
          if (
            !customParam.dropdownOptions ||
            customParam.dropdownOptions.length === 0
          ) {
            errors.push({
              field: "dropdownOptions",
              message: `At least one dropdown option is required for parameter "${
                customParam.name || "Untitled"
              }"`,
              customParamKey: customParam.key,
            });
          } else {
            const emptyOptions = customParam.dropdownOptions.filter(
              (option, index) => !option || option.trim() === ""
            );

            if (emptyOptions.length > 0) {
              errors.push({
                field: "dropdownOptions",
                message: `All dropdown options must have values for parameter "${
                  customParam.name || "Untitled"
                }"`,
                customParamKey: customParam.key,
              });
            }

            const optionValues = customParam.dropdownOptions
              .map((opt) => opt?.trim().toLowerCase())
              .filter((opt) => opt && opt !== "");

            const duplicateOptions = optionValues.filter(
              (opt, index) => optionValues.indexOf(opt) !== index
            );

            if (duplicateOptions.length > 0) {
              errors.push({
                field: "dropdownOptions",
                message: `Duplicate dropdown options found for parameter "${
                  customParam.name || "Untitled"
                }"`,
                customParamKey: customParam.key,
              });
            }
          }
        }

        if (!customParam.setting) {
          errors.push({
            field: "customParameterSetting",
            message: `Setting is required for custom parameter "${
              customParam.name || "Untitled"
            }"`,
            customParamKey: customParam.key,
          });
        }

        if (customParam.setting && customParam.setting !== "user_defined") {
          if (customParam.type === "file_upload") {
            const hasValidFile =
              customParam.values &&
              customParam.values.some(
                (val: any) =>
                  val instanceof File ||
                  (typeof val === "string" && val.trim() !== "")
              );

            if (!hasValidFile && customParam.setting === "single_publisher") {
              errors.push({
                field: "customParameterValue",
                message: `File is required for parameter "${
                  customParam.name || "Untitled"
                }"`,
                customParamKey: customParam.key,
              });
            }
          } else {
            const hasValidValue =
              customParam.values &&
              customParam.values.some(
                (val) => val && val.toString().trim() !== ""
              );

            if (!hasValidValue && customParam.setting === "single_publisher") {
              errors.push({
                field: "customParameterValue",
                message: `Value is required for parameter "${
                  customParam.name || "Untitled"
                }"`,
                customParamKey: customParam.key,
              });
            }
          }

          if (customParam.setting === "multi_publisher") {
            if (!customParam.values || customParam.values.length === 0) {
              errors.push({
                field: "customParameterValue",
                message: `At least one value is required for parameter "${
                  customParam.name || "Untitled"
                }"`,
                customParamKey: customParam.key,
              });
            }
          }
        }
      });

      // Duplicate name check only among enabled custom params
      const customParamNames = state.customParameters
        .filter((param) => param.enabled !== false)
        .map((param) => param.name?.trim().toLowerCase())
        .filter((name) => name && name !== "");

      const duplicateCustomNames = customParamNames.filter(
        (name, index) => customParamNames.indexOf(name) !== index
      );

      if (duplicateCustomNames.length > 0) {
        errors.push({
          field: "customParameters",
          message: "Duplicate custom parameter names are not allowed",
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static validateStepFive(state: PublisherStore): StepValidationResult {
    const errors: ValidationError[] = [];

    // Canvas builder step validation - optional for now
    // Can add validation for selectedInterface, selectedAdSpecs, etc. if needed

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static validateStepSix(state: PublisherStore): StepValidationResult {
    const errors: ValidationError[] = [];

    const stepOneResult = this.validateStepOne(state);
    const stepTwoResult = this.validateStepTwo(state);
    const stepThreeResult = this.validateStepThree(state);
    const stepFourResult = this.validateStepFour(state);

    errors.push(
      ...stepOneResult.errors,
      ...stepTwoResult.errors,
      ...stepThreeResult.errors,
      ...stepFourResult.errors
    );

    if (state.adTypes.length === 0) {
      errors.push({
        field: "general",
        message: "No ad types have been configured",
      });
    }

    let totalAdInputs = 0;
    state.adTypes.forEach((adType) => {
      totalAdInputs += adType.adInputs.length;
    });

    if (totalAdInputs === 0) {
      errors.push({
        field: "general",
        message: "No ad inputs have been configured across all publishers",
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static validateStep(
    stepNumber: number,
    state: PublisherStore
  ): StepValidationResult {
    switch (stepNumber) {
      case 0:
        return this.validateStepOne(state);
      case 1:
        return this.validateStepTwo(state);
      case 2:
        return this.validateStepThree(state);
      case 3:
        return this.validateStepFour(state);
      case 4:
        return this.validateStepFive(state);
      case 5:
        return this.validateStepSix(state);
      default:
        return {
          isValid: false,
          errors: [
            {
              field: "step",
              message: "Invalid step number",
            },
          ],
        };
    }
  }

  static formatErrors(errors: ValidationError[]): string[] {
    return errors.map((error) => {
      let message = error.message;

      if (error.publisherIndex !== undefined) {
        message += ` (Publisher ${error.publisherIndex + 1})`;
      }

      if (error.adInputIndex !== undefined) {
        message += ` (Ad Type ${error.adInputIndex + 1})`;
      }

      return message;
    });
  }

  static isReadyForSubmission(state: PublisherStore): boolean {
    const finalValidation = this.validateStepSix(state);
    return finalValidation.isValid;
  }
}
